<template>
  <v-container fluid class="sci-page-wrapper pa-0" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        
        <!-- HEADER -->
        <div class="sci-page-header pb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 class="sci-page-title-only">Painel de Liberação de OS</h1>
            <p class="text-sm text-slate-500 font-medium mt-1">Autorização de fornecimento de peças e serviços e baixa de estoque</p>
          </div>
          <!-- Identificação do Fornecedor -->
          <div v-if="providerName" class="bg-orange-50 border border-orange-200 px-4 py-2 rounded-xl flex items-center gap-3">
            <v-icon color="orange">mdi-storefront-outline</v-icon>
            <div>
              <p class="text-[9px] font-black text-orange-400 uppercase tracking-wider mb-0.5">FORNECEDOR LOGADO</p>
              <p class="text-xs font-black text-slate-700 uppercase tracking-tight">{{ providerName }}</p>
            </div>
          </div>
        </div>

        <!-- FILTROS E BUSCA -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-magnify</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">PESQUISAR E FILTRAR</h2>
          </div>
          <div class="p-8">
            <v-row align="center">
              <!-- SELECIONAR FORNECEDOR (APENAS PARA ADMINS) -->
              <v-col cols="12" md="6" v-if="isAdmin">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                <v-autocomplete
                  :items="providers"
                  placeholder="SELECIONE O FORNECEDOR..."
                  v-model="filter.provider_id"
                  item-value="provider_id"
                  item-text="provider_name"
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                  clearable
                  @change="sendSearch"
                />
              </v-col>

              <!-- BUSCA GERAL (PLACA OU OS) -->
              <v-col cols="12" :md="isAdmin ? 6 : 12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Buscar Ordem de Serviço</label>
                <v-text-field
                  v-model="searchQuery"
                  placeholder="DIGITE O NÚMERO DA OS OU PLACA DO VEÍCULO..."
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                  clearable
                  prepend-inner-icon="mdi-magnify"
                />
              </v-col>
            </v-row>
          </div>
        </section>

        <!-- GRID DE ORDENS PENDENTES (PRÉ-VENDA) -->
        <section class="space-y-4">
          <div class="flex items-center justify-between pb-2 border-b border-slate-200">
            <div class="flex items-center gap-2">
              <v-icon color="orange" small>mdi-clock-outline</v-icon>
              <h2 class="text-slate-700 font-bold uppercase tracking-wider text-xs">Aguardando Liberação ({{ filteredPendingOs.length }})</h2>
            </div>
            <v-btn icon color="slate-600" @click="sendSearch" :loading="loader">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>

          <div v-if="filteredPendingOs.length === 0" class="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400">
            <v-icon size="48" color="slate-200">mdi-clipboard-check-outline</v-icon>
            <p class="mt-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Nenhuma Ordem de Serviço aguardando liberação</p>
          </div>

          <div class="grid grid-cols-1 gap-6" v-else>
            <div 
              v-for="os in filteredPendingOs" 
              :key="os.os_id"
              class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
            >
              <!-- Card Header -->
              <div class="px-6 py-4 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div class="flex items-center gap-4">
                  <span class="bg-orange-500 text-white font-black text-xs px-3 py-1.5 rounded-lg shadow-sm">
                    OS #{{ String(os.os_number).padStart(4, '0') }}
                  </span>
                  <div>
                    <h3 class="text-sm font-bold text-slate-800 uppercase">{{ os.prefecture_social_name }}</h3>
                    <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">DATA: {{ convertDate(os.os_date) }} | RESPONSÁVEL: {{ os.os_responsible || 'NÃO INFORMADO' }}</p>
                  </div>
                </div>
                
                <div class="flex items-center gap-6">
                  <div class="text-right">
                    <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">VALOR TOTAL</p>
                    <p class="text-lg font-black text-slate-700">R$ {{ os.os_total }}</p>
                  </div>
                  <v-chip color="amber" text-color="amber-darken-4" small font-weight="black" class="text-[9px] font-black uppercase tracking-widest bg-amber-50 border border-amber-200">
                    {{ os.os_status }}
                  </v-chip>
                </div>
              </div>

              <!-- Card Body: Vehicle details & Items list -->
              <div class="p-6">
                <!-- Vehicle Info -->
                <div class="mb-6 p-4 bg-slate-50/60 rounded-xl border border-slate-150 flex items-center gap-4">
                  <div class="bg-white border border-slate-200 p-2.5 rounded-lg text-slate-700 flex items-center justify-center font-mono font-black text-sm uppercase tracking-widest shadow-sm">
                    {{ os.vehicle_board }}
                  </div>
                  <div>
                    <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">VEÍCULO DA FROTA</p>
                    <p class="text-xs font-bold text-slate-600 uppercase">{{ os.brand_desc }} - {{ os.model_desc }}</p>
                  </div>
                </div>

                <!-- Items Table -->
                <div class="border border-slate-150 rounded-xl overflow-hidden mb-6">
                  <table class="w-full text-left border-collapse">
                    <thead>
                      <tr class="bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-400 border-b border-slate-150">
                        <th class="py-3 px-4">Item / Serviço</th>
                        <th class="py-3 px-4">Tipo</th>
                        <th class="py-3 px-4 text-center">Quantidade</th>
                        <th class="py-3 px-4 text-right">P. Unitário</th>
                        <th class="py-3 px-4 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-xs font-medium text-slate-600">
                      <tr v-for="(item, idx) in getOsItems(os.os_items)" :key="idx" class="hover:bg-slate-50/30">
                        <td class="py-3.5 px-4 font-bold text-slate-700 uppercase">
                          {{ item.isServico ? item.servico : getItemName(item.peca_id) }}
                        </td>
                        <td class="py-3.5 px-4">
                          <span 
                            class="px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider"
                            :class="item.isServico ? 'bg-slate-100 text-slate-500' : 'bg-orange-50 text-orange-600 border border-orange-100'"
                          >
                            {{ item.isServico ? 'SERVIÇO' : 'PEÇA' }}
                          </span>
                        </td>
                        <td class="py-3.5 px-4 text-center font-bold">{{ item.quantidade || '1' }}</td>
                        <td class="py-3.5 px-4 text-right">R$ {{ item.preco }}</td>
                        <td class="py-3.5 px-4 text-right font-bold text-slate-700">R$ {{ item.total || item.preco }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Actions -->
                <div class="flex justify-end gap-4 border-t border-slate-100 pt-6">
                  <button 
                    @click="rejectOs(os.os_id)"
                    class="bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl px-5 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 border border-rose-200"
                  >
                    <v-icon small color="currentColor">mdi-close-circle-outline</v-icon>
                    RECUSAR ORDEM
                  </button>
                  
                  <button 
                    @click="releaseOs(os.os_id)"
                    class="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-6 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                  >
                    <v-icon small color="white">mdi-check-decagram</v-icon>
                    LIBERAR FORNECIMENTO
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- TABELA DE HISTÓRICO RECENTE -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3 bg-slate-800">
            <v-icon color="orange" small>mdi-history</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Histórico Recente (Hoje)</h2>
          </div>
          <div class="p-0">
            <v-data-table
              :headers="historyHeaders"
              :items="filteredHistoryOs"
              :loading="loader"
              hide-default-footer
              class="custom-table"
              no-data-text="Nenhuma liberação registrada hoje"
            >
              <template v-slot:[`item.os_number`]="{ item }">
                <span class="text-xs font-bold text-slate-700">OS #{{ String(item.os_number).padStart(4, '0') }}</span>
              </template>
              <template v-slot:[`item.os_date`]="{ item }">
                <span class="text-xs text-slate-600 font-semibold">{{ convertDate(item.os_date) }}</span>
              </template>
              <template v-slot:[`item.prefecture_social_name`]="{ item }">
                <span class="font-bold text-slate-700 uppercase">{{ item.prefecture_social_name }}</span>
              </template>
              <template v-slot:[`item.vehicle_board`]="{ item }">
                <span class="text-xs font-bold text-slate-700 uppercase">{{ item.vehicle_board }}</span>
              </template>
              <template v-slot:[`item.os_total`]="{ item }">
                <span class="text-sm font-black text-slate-700">R$ {{ item.os_total }}</span>
              </template>
              <template v-slot:[`item.os_status`]="{ item }">
                <span 
                  class="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                  :class="item.os_status === 'CONCLUIDO' ? 'bg-emerald-50 text-emerald-600 border border-emerald-250' : 'bg-rose-50 text-rose-600 border border-rose-250'"
                >
                  {{ item.os_status === 'CONCLUIDO' ? 'LIBERADO' : 'RECUSADO' }}
                </span>
              </template>
            </v-data-table>
          </div>
        </section>

        <!-- Snackbar -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="5000"
          rounded="xl"
          elevation="24"
          class="mb-8"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
          </div>
          <template v-slot:action="{ attrs }">
            <v-btn dark icon v-bind="attrs" @click="snack.show = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    loader: true,
    providers: [],
    biddingItems: [],
    osList: [],
    searchQuery: "",
    filter: {
      provider_id: null,
      startDate: "",
      endDate: "",
    },
    snack: {
      show: false,
      message: "",
      color: "",
    },
    historyHeaders: [
      { text: "Número OS", value: "os_number" },
      { text: "Prefeitura", value: "prefecture_social_name" },
      { text: "Veículo", value: "vehicle_board" },
      { text: "Data da OS", value: "os_date" },
      { text: "Valor Total", value: "os_total" },
      { text: "Responsável", value: "os_responsible" },
      { text: "Status", value: "os_status", align: "center" },
    ],
  }),
  computed: {
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    providerName() {
      if (this.$store.getters.getUserData.table_key === 'providers') {
        const found = this.providers.find(p => p.provider_id === this.$store.getters.getUserData.table_id);
        return found ? found.provider_name : "Fornecedor Credenciado";
      }
      return "";
    },
    filteredPendingOs() {
      return this.osList.filter(os => {
        const matchesStatus = os.os_status === 'ABERTO' || os.os_status === 'EM ANDAMENTO';
        
        let matchesQuery = true;
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase();
          const osNum = String(os.os_number).toLowerCase();
          const board = String(os.vehicle_board).toLowerCase();
          const pref = String(os.prefecture_social_name).toLowerCase();
          matchesQuery = osNum.includes(q) || board.includes(q) || pref.includes(q);
        }

        return matchesStatus && matchesQuery;
      });
    },
    filteredHistoryOs() {
      const todayStr = new Date().toISOString().substring(0, 10);
      return this.osList.filter(os => {
        const matchesStatus = os.os_status === 'CONCLUIDO' || os.os_status === 'CANCELADO';
        const matchesDate = os.updated_at && os.updated_at.substring(0, 10) === todayStr;
        return matchesStatus && matchesDate;
      });
    },
  },
  created() {
    this.$http.get("/parts-and-services/providers").then((r) => {
      this.providers = r.data;
    });

    if (this.$store.getters.getUserData.table_key === 'providers') {
      this.filter.provider_id = this.$store.getters.getUserData.table_id;
    }

    this.loadAllBiddingItems();
    this.sendSearch();
  },
  methods: {
    sendSearch() {
      this.loader = true;
      this.$http
        .post("/parts-and-services/os/conferences", this.filter)
        .then((r) => {
          this.osList = r.data;
          this.loader = false;
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
        });
    },
    loadAllBiddingItems() {
      // Carrega os itens das licitações para obter os nomes reais das peças
      this.$http.get("/parts-and-services/biddings/items").then((r) => {
        this.biddingItems = r.data;
      });
    },
    getOsItems(itemsStr) {
      try {
        return JSON.parse(itemsStr || "[]");
      } catch (e) {
        return [];
      }
    },
    getItemName(pecaId) {
      if (!pecaId) return "Peça / Produto";
      const found = this.biddingItems.find(item => item.item_id === pecaId);
      return found ? `${found.name} - ${found.marca || ''}` : `Peça #${pecaId}`;
    },
    convertDate(date) {
      if (date) {
        date = date.substring(0, 10).replace("-", "").replace("-", "");
        const year = date.substring(0, 4);
        const month = date.substring(4, 6);
        const day = date.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      return "";
    },
    releaseOs(id) {
      if (confirm("Deseja realmente aprovar e liberar o fornecimento desta Ordem de Serviço? Esta ação efetuará a baixa no estoque do contrato.")) {
        this.loader = true;
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/parts-and-services/os/release/${id}`)
          .then((r) => {
            this.snack.show = true;
            if (r.data.success) {
              this.snack.message = r.data.result;
              this.snack.color = "success";
            } else {
              this.snack.message = r.data.error;
              this.snack.color = "error";
            }
            this.sendSearch();
            this.$store.dispatch("setLoader", false);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    rejectOs(id) {
      if (confirm("Deseja realmente recusar e cancelar esta Ordem de Serviço?")) {
        this.loader = true;
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/parts-and-services/os/reject/${id}`)
          .then((r) => {
            this.snack.show = true;
            if (r.data.success) {
              this.snack.message = r.data.result;
              this.snack.color = "success";
            } else {
              this.snack.message = r.data.error;
              this.snack.color = "error";
            }
            this.sendSearch();
            this.$store.dispatch("setLoader", false);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep table { 
  background-color: transparent !important; 
}

.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 900 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.1em !important;
  background-color: #f8fafc !important;
  border-bottom: 2px solid #f1f5f9 !important;
  height: 52px !important;
}

.custom-table ::v-deep td { 
  border-bottom: 1px solid #f8fafc !important;
  color: #475569 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}

.custom-select ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 44px !important;
}

.custom-select ::v-deep input {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  text-transform: uppercase !important;
}
</style>
